import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Loader2, Phone, ArrowRight } from "lucide-react";
import TopBar from "@/components/TopBar";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, BUSINESS } from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

/**
 * Minimal markdown-ish renderer:
 *  - ## H2 / ### H3
 *  - Bold **text**
 *  - Tables (pipe-separated)
 *  - Unordered lists with -
 *  - Ordered lists with 1.
 *  - Paragraphs
 */
const renderContent = (md) => {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let i = 0;
  const inlineBold = (s) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((p, idx) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={idx}>{p.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={idx}>{p}</React.Fragment>
      )
    );

  while (i < lines.length) {
    const line = lines[i];
    // Headings
    if (line.startsWith("### ")) {
      blocks.push(<h3 key={i}>{inlineBold(line.slice(4))}</h3>);
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push(<h2 key={i}>{inlineBold(line.slice(3))}</h2>);
      i++;
      continue;
    }
    // Table
    if (line.startsWith("|") && lines[i + 1] && /^\|[-: |]+\|$/.test(lines[i + 1].trim())) {
      const header = line.split("|").slice(1, -1).map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      blocks.push(
        <table key={`t-${i}`}>
          <thead>
            <tr>{header.map((h, idx) => <th key={idx}>{inlineBold(h)}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, ridx) => (
              <tr key={ridx}>{r.map((c, cidx) => <td key={cidx}>{inlineBold(c)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      );
      continue;
    }
    // Unordered list
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push(
        <ul key={`ul-${i}`}>
          {items.map((it, idx) => <li key={idx}>{inlineBold(it)}</li>)}
        </ul>
      );
      continue;
    }
    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push(
        <ol key={`ol-${i}`}>
          {items.map((it, idx) => <li key={idx}>{inlineBold(it)}</li>)}
        </ol>
      );
      continue;
    }
    // Blank line
    if (line.trim() === "") {
      i++;
      continue;
    }
    // Paragraph (gather adjacent non-blank, non-special lines)
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].startsWith("#") &&
      !lines[i].startsWith("- ") &&
      !/^\d+\.\s/.test(lines[i]) &&
      !lines[i].startsWith("|")
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(<p key={`p-${i}`}>{inlineBold(paraLines.join(" "))}</p>);
  }
  return blocks;
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    api
      .get(`/blog/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Post not found" : "Failed to load post"))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="blog-detail-page">
      {post && (
        <SEO
          title={`${post.title}, Corona Gas Blog`}
          description={post.excerpt}
          url={`https://coronagas.ca/blog/${post.slug}`}
          image={post.cover_image}
          type="article"
          keywords={(post.tags || []).join(", ")}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: post.cover_image,
            datePublished: post.published_at,
            author: { "@type": "Organization", name: post.author || "Corona Gas" },
            publisher: {
              "@type": "Organization",
              name: "Corona Gas",
              logo: { "@type": "ImageObject", url: "https://coronagas.ca/logo.png" },
            },
            mainEntityOfPage: `https://coronagas.ca/blog/${post.slug}`,
          }}
        />
      )}
      <TopBar />
      <Navbar />

      {loading && (
        <div className="flex justify-center py-32" data-testid="post-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#E50914]" />
        </div>
      )}

      {error && (
        <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="post-error">
          <h1 className="font-[Manrope] font-bold text-2xl mb-3 text-[#0F0F0F]">{error}</h1>
          <Link to="/blog" className="btn-coral mt-4">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </div>
      )}

      {post && (
        <>
          {/* Header */}
          <header className="relative cg-pink-bg py-14 lg:py-20 overflow-hidden">
            <div className="absolute inset-0 cg-halftone-light opacity-50 pointer-events-none" />
            <div className="relative max-w-3xl mx-auto px-4">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[#E50914] font-semibold mb-6 hover:gap-3 transition-all"
                data-testid="back-to-blog"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.map((t) => (
                    <span key={t} className="bg-white text-[#E50914] text-xs font-semibold px-3 py-1 rounded-full border border-[#EAEAEA]">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <h1 className="font-[Manrope] font-extrabold text-[#0F0F0F] text-3xl sm:text-4xl lg:text-5xl leading-tight mb-5">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-5 text-sm text-[#64748B]">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#E50914]" />
                  {formatDate(post.published_at)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[#E50914]" />
                  {post.read_time} min read
                </span>
                <span>By {post.author}</span>
              </div>
            </div>
          </header>

          {/* Cover image */}
          <div className="max-w-4xl mx-auto px-4 -mt-8">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-[300px] sm:h-[400px] object-cover rounded-2xl shadow-xl"
              data-testid="post-cover"
            />
          </div>

          {/* Content */}
          <article className="max-w-3xl mx-auto px-4 py-12 lg:py-16">
            <div className="blog-prose" data-testid="post-content">{renderContent(post.content)}</div>

            {/* CTA */}
            <div className="mt-12 bg-[#FCE7E9] border border-[#EAEAEA] rounded-2xl p-8 text-center">
              <h3 className="font-[Manrope] font-extrabold text-[#0F0F0F] text-2xl mb-3">
                Have an HVAC question?
              </h3>
              <p className="text-[#475569] mb-5">
                Our team is happy to help, free quotes, no pressure.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/#contact" className="btn-coral" data-testid="post-cta-contact">
                  Get a Free Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={BUSINESS.phoneHref} className="btn-outline-coral" data-testid="post-cta-call">
                  <Phone className="w-4 h-4" />
                  {BUSINESS.phone}
                </a>
              </div>
            </div>
          </article>
        </>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
